import type { CSSProperties } from 'react';

import styles from './Icon.module.css';
import type { IconName } from './iconNames';

export interface IconProps {
  /** Glyph name from the icomoon set (without the `icon-` prefix). */
  name: IconName;
  /** Rendered size in px (maps to font-size). Defaults to the inherited font size. */
  size?: number;
  /** Overrides the glyph color. Defaults to `currentColor`. */
  color?: string;
  /**
   * Accessible name. When provided the glyph is exposed as an image with this label;
   * otherwise it is treated as decorative (`aria-hidden`).
   */
  label?: string;
  className?: string;
}

/**
 * Renders a single icomoon glyph. Font ships with `@smartsash/ui/icons.css`
 * (import once at the app root); size/color are set via component-local CSS vars.
 *
 * @interim Ported from the reference apps; replaceable by the UI Kit without API breakage.
 */
export function Icon({ name, size, color, label, className }: IconProps) {
  const style: CSSProperties = {};
  if (size !== undefined) (style as Record<string, string>)['--icon-size'] = `${String(size)}px`;
  if (color !== undefined) (style as Record<string, string>)['--icon-color'] = color;

  const classes = [styles.icon, `icon-${name}`, className].filter(Boolean).join(' ');
  const a11y = label ? { role: 'img', 'aria-label': label } : { 'aria-hidden': true };

  return <i className={classes} style={style} {...a11y} />;
}
